import styled from "styled-components";

import { caseInvoiceUrl } from "@pages/AdminDashboard/externalLinks";
import { formatAdminDate } from "@pages/AdminDashboard/formatAdminDate";
import { InvoiceLink } from "@pages/AdminDashboard/InvoiceLink";
import { caseStatusLabel } from "@pages/AdminDashboard/plainLanguageLabels";
import { UserEmailLink } from "@pages/AdminDashboard/UserEmailLink";
import { telehealthCaseUrl } from "@pages/AdminDashboard/telehealthLinks";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { DataTableColumn } from "@pages/AdminDashboard/ui/DataTable";
import { ExternalLink } from "@pages/AdminDashboard/ui/DataTable/cells";
import { StatusBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { Case, Trip } from "@types-runway/case";

const { color, typeScale } = adminTokens;
const [, spaceSm] = adminTokens.spacing;
const noValue = "—";
const noTripDate = "No trip date";
const linkGap = 10;
const emailMaxWidth = 108;
const caseColumnMinWidth = 108;
const nonBreakingSpace = " ";

// The telehealth Case and, when its payment vendor has a page for it, the invoice (CVIEW-R9,
// XCUT-R26, R27).
const CaseLinks = ({ row }: { row: Case }) => {
  const invoiceUrl = caseInvoiceUrl(row);
  if (!row.caseId && !invoiceUrl) return <>{noValue}</>;

  return (
    <Links>
      {row.caseId && <ExternalLink href={telehealthCaseUrl(row.caseId)}>Consult</ExternalLink>}
      {invoiceUrl && <ExternalLink href={invoiceUrl}>Invoice</ExternalLink>}
    </Links>
  );
};

// The admin row of GET /v1/search/case carries the patient's User (cases-api, ENG-870); the
// shared Case type does not declare it.
type CaseSearchRow = Case & { User?: { email: string } | null };

const Stack = styled.div`
  display: flex;
  min-width: ${caseColumnMinWidth}px;
  flex-direction: column;
  align-items: flex-start;
  padding: ${spaceSm}px 0;
`;

const CaseNumber = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
`;

// An email can be long; it breaks inside the word rather than widening the column.
const PatientEmail = styled.span`
  max-width: ${emailMaxWidth}px;
  color: ${color.inkSoft};
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const Links = styled.div`
  display: flex;
  gap: ${linkGap}px;
  white-space: nowrap;
`;

const Clinician = styled.span`
  color: ${color.inkSoft};
`;

export interface CaseColumnHandlers {
  isConditionsOpen: (caseRecord: Case) => boolean;
  onToggleConditions: (caseRecord: Case) => void;
}

// CVIEW-R4: the destination, a dot and the start date; a Case with no trip says so.
const describeTrip = (trip?: Trip & { destination?: string }) => {
  if (!trip?.from) return noTripDate;

  // A narrow column breaks the text after the destination, never inside the date.
  const date = formatAdminDate(trip.from).replace(/ /g, nonBreakingSpace);

  return trip.destination ? `${trip.destination} · ${date}` : date;
};

// FIND-E14: one case per row — number and patient, status, paid flag, trip, clinician and the
// links to the telehealth case and its invoice (CVIEW-R3…R5, R8, R9).
export const buildCaseColumns = ({
  isConditionsOpen,
  onToggleConditions,
}: CaseColumnHandlers): DataTableColumn<Case>[] => [
  {
    id: "case",
    header: "Case",
    render: (caseRecord) => {
      const { id } = caseRecord;
      const patientEmail = (caseRecord as CaseSearchRow).User?.email;

      return (
        <Stack>
          <CaseNumber>{id}</CaseNumber>
          {patientEmail && (
            <PatientEmail>
              <UserEmailLink email={patientEmail} />
            </PatientEmail>
          )}
        </Stack>
      );
    },
  },
  {
    id: "status",
    header: "Status",
    phonePlacement: "badge",
    render: ({ case_status: status }) =>
      status ? (
        <StatusBadge status={status} label={caseStatusLabel(status)} />
      ) : (
        <StatusBadge status={noValue} />
      ),
  },
  {
    id: "paid",
    header: "Paid",
    render: ({ paid, invoiceId }) => (
      <InvoiceLink invoiceId={invoiceId}>{paid ? "✓ Paid" : "Unpaid"}</InvoiceLink>
    ),
  },
  { id: "trip", header: "Trip", render: ({ trip }) => describeTrip(trip) },
  {
    id: "clinician",
    header: "Clinician",
    render: ({ clinician }) => <Clinician>{clinician?.fullname ?? noValue}</Clinician>,
  },
  {
    id: "links",
    header: "Links",
    render: (row) => <CaseLinks row={row} />,
  },
  {
    id: "actions",
    header: "Actions",
    align: "end",
    isHeaderHidden: true,
    render: (caseRecord) => (
      <Button look="secondary" onClick={() => onToggleConditions(caseRecord)}>
        {isConditionsOpen(caseRecord) ? "Hide conditions" : "Conditions"}
      </Button>
    ),
  },
];
